import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { LineOptions, lineOptionsState } from '~/state/atoms/toolsAtoms';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * Returns a function that sets the line tool's mode, draw or erase, from the
 * current mode. A mode change re-arms the tool, and a re-armed tool has no
 * line, so while a line is in progress the mode stays and a warning says
 * why.
 */
export function useSwitchLineMode(): (
    next: (mode: LineOptions['mode']) => 'draw' | 'erase'
) => void {
    const intl = useIntl();
    const setLineOptions = useSetRecoilState(lineOptionsState);
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;

    return useCallback(
        (next) => {
            if (toolManager?.isActiveToolActionInProgress()) {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'toolbar.contouring.erase.lineInProgress',
                    }),
                    { variant: 'warning' }
                );
                return;
            }
            setLineOptions((options) => ({
                ...options,
                mode: next(options.mode),
            }));
        },
        [intl, setLineOptions, toolManager]
    );
}
